import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, Renderer2, NgZone, OnDestroy } from '@angular/core';
import { ComponentBase } from 'src/app/main/dynamic/component-base';
import { DynamicEvent } from 'src/app/main/dynamic/dynamic.event';
import { environment } from 'src/environments/environment';
import { SharedDataService } from 'src/app/service/shared-data.service';

declare var google: any;

@Component({
  selector: 'app-map-view',
  templateUrl: './map-view.component.html',
  styleUrls: ['./map-view.component.scss']
})
export class MapViewComponent extends ComponentBase implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;

  private map: any;
  private markers: any[] = [];
  private geocoder: any;
  private isApiLoaded = false;
  public isServicesInitialized = false;
  private initRetryCount = 0;
  private readonly maxRetries = 5;
  private initialValue: string;

  // Propriedades do mapa
  mapCenter = { lat: -2.5297, lng: -44.3028 }; // São Luís, MA
  mapZoom = 14;
  mapType = 'roadmap';

  // Dados dos locais (agora dinâmicos)
  locations: any[] = [];
  selectedLocation: any = null;

  // Cores para os marcadores
  private markerColors = ['#28a745', '#e83e8c', '#dc3545', '#fd7e14', '#6f42c1', '#20c997', '#17a2b8', '#6c757d'];

  constructor(
    private renderer: Renderer2,
    private ngZone: NgZone,
    private sharedDataService: SharedDataService
  ) {
    super();
  }

  private get valueChanged(): boolean {
    return this.initialValue != this.attribute.value;
  }

  ngOnInit() {
    this.initialValue = this.attribute.value;
    this.shareData();
    this.loadGoogleMapsAPI();
    this.loadAddressData();
  }

  ngAfterViewInit() {
    console.log('Map View component initialized');
    // Caso os serviços já tenham sido inicializados antes da view estar pronta,
    // inicializa o mapa agora que o container existe no DOM
    if (this.isServicesInitialized && !this.map) {
      this.initializeMap();
    }
  }

  ngOnDestroy() {
    if(this.attribute.uid) {
      this.sharedDataService.remove(this.attribute.uid);
    }
  }

  private loadAddressData(): void {
    // Limpa locais existentes
    this.locations = [];

    // Lê exclusivamente dos filhos de this.attribute onde key.toLowerCase() === 'addresses'
    const addressesContainer = this.attribute?.attributes?.find((attr: any) => {
      return typeof attr?.key === 'string' && attr.key.toLowerCase() === 'addresses';
    });

    if (!addressesContainer || !Array.isArray(addressesContainer.attributes)) {
      return;
    }

    addressesContainer.attributes.forEach((childAttr: any) => {
      const hasValue = typeof childAttr?.value === 'string' && childAttr.value.trim().length > 0;
      if (hasValue) {
        this.addLocationFromAddress(childAttr.value, childAttr.key, childAttr);
      }
    });
  }

  private addLocationFromAddress(address: string, key?: string, source?: any): void {
    if (!address || address.trim() === '') return;

    // Verifica se já existe um local com este endereço
    const existingLocation = this.locations.find(loc => loc.address === address);
    if (existingLocation) return;

    const locationName = key || this.extractLocationName(address);
    const disabled = this.isAddressDisabled(source);
    const foundAddressText = this.readFoundAddressText(source);
    const color = disabled ? '#9aa0a6' : this.getNextMarkerColor();

    const location = {
      name: locationName,
      address: address,
      position: null, // Será preenchido após geocoding
      color: color,
      description: address,
      isGeocoded: false,
      disabled: disabled,
      foundAddressText: foundAddressText
    };

    this.locations.push(location);
    
    // Se o geocoder estiver disponível, faz o geocoding
    if (this.geocoder) {
      this.geocodeAddress(location);
    }
  }

  private isAddressDisabled(source: any): boolean {
    if (!source) return false;
    const style = this.getOptionByKey(source, 'Style');
    return typeof style === 'string' && style.trim().toLowerCase() === 'disabled';
  }

  private readFoundAddressText(source: any): string {
    if (!source) return '';
    const text = this.getOptionByKey(source, 'FoundAddressText');
    return typeof text === 'string' ? text.trim() : '';
  }

  private extractLocationName(address: string): string {
    // Extrai o nome da rua do endereço
    const parts = address.split(',');
    if (parts.length > 0) {
      return parts[0].trim();
    }
    return 'Local';
  }

  private getNextMarkerColor(): string {
    return this.markerColors[this.locations.length % this.markerColors.length];
  }

  private geocodeAddress(location: any): void {
    if (!this.geocoder) return;

    this.geocoder.geocode({ address: location.address }, (results: any, status: any) => {
      this.ngZone.run(() => {
        if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
          const result = results[0];
          location.position = {
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng()
          };
          location.isGeocoded = true;
          
          // Atualiza o mapa se já foi inicializado
          if (this.map) {
            this.updateMapMarkers();
            this.fitMapToMarkers();
          }
          
          console.log(`Endereço geocodificado: ${location.address} -> ${location.position.lat}, ${location.position.lng}`);
        } else {
          console.warn(`Falha no geocoding para: ${location.address}`);
        }
      });
    });
  }

  private updateMapMarkers(): void {
    // Remove marcadores existentes
    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];

    // Adiciona novos marcadores apenas para locais geocodificados
    this.locations.filter(loc => loc.isGeocoded).forEach((location, index) => {
      const marker = new google.maps.Marker({
        position: location.position,
        map: this.map,
        title: location.name,
        icon: this.createCustomMarker(location.disabled),
        animation: google.maps.Animation.DROP
      });

      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="padding: 10px; max-width: 220px;">
            <h6 style="margin: 0 0 5px 0; color: #333;">${location.name}</h6>
            <p style="margin: 0 0 8px 0; font-size: 12px; color: #666;">${location.description}</p>
            <a href="https://www.google.com/maps/dir/?api=1&destination=${location.position?.lat},${location.position?.lng}"
               target="_blank" rel="noopener"
               style="font-size: 12px; color: #1a73e8; text-decoration: none;">
              Abrir no Google Maps
            </a>
          </div>
        `
      });

      marker.addListener('click', () => {
        infoWindow.open(this.map, marker);
      });

      this.markers.push(marker);
    });
  }

  private fitMapToMarkers(): void {
    if (this.markers.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    this.markers.forEach(marker => {
      bounds.extend(marker.getPosition());
    });

    this.map.fitBounds(bounds);
    
    // Ajusta o zoom se necessário
    const listener = google.maps.event.addListener(this.map, 'idle', () => {
      if (this.map.getZoom() > 16) {
        this.map.setZoom(16);
      }
      google.maps.event.removeListener(listener);
    });
  }

  private loadGoogleMapsAPI(): void {
    // Verifica se a API já está carregada
    if (this.isGoogleMapsLoaded()) {
      console.log('Google Maps API já estava carregada');
      this.initializeServicesWithRetry();
      return;
    }

    // Verifica se já há um script sendo carregado
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      console.log('Google Maps API já está sendo carregada');
      this.waitForApiLoad();
      return;
    }

    console.log('Carregando Google Maps API...');
    const script = this.renderer.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&libraries=places,geocoding&language=pt-BR&region=BR&callback=__googleMapsCallback`;
    script.async = true;
    script.defer = true;
    
    // Cria callback global
    (window as any).__googleMapsCallback = () => {
      console.log('Google Maps API carregada via callback');
      this.isApiLoaded = true;
      this.initializeServicesWithRetry();
    };

    script.onerror = (error) => {
      console.error('Erro ao carregar Google Maps API:', error);
    };

    this.renderer.appendChild(document.head, script);
  }

  private isGoogleMapsLoaded(): boolean {
    return typeof google !== 'undefined' && 
           google.maps && 
           google.maps.Map;
  }

  private waitForApiLoad(): void {
    // Verifica periodicamente se a API foi carregada
    const checkInterval = setInterval(() => {
      if (this.isGoogleMapsLoaded()) {
        clearInterval(checkInterval);
        this.initializeServicesWithRetry();
      }
    }, 100);

    // Timeout após 10 segundos
    setTimeout(() => {
      clearInterval(checkInterval);
      if (!this.isGoogleMapsLoaded()) {
        console.error('Timeout: Google Maps API não carregou em 10 segundos');
      }
    }, 10000);
  }

  private initializeServicesWithRetry(): void {
    if (this.isServicesInitialized) {
      return;
    }

    if (!this.isGoogleMapsLoaded()) {
      this.initRetryCount++;
      if (this.initRetryCount <= this.maxRetries) {
        console.log(`Tentativa ${this.initRetryCount}/${this.maxRetries} - Aguardando Google Maps API...`);
        setTimeout(() => this.initializeServicesWithRetry(), 200 * this.initRetryCount);
      } else {
        console.error('Falha ao inicializar Google Maps após múltiplas tentativas');
      }
      return;
    }

    try {
      this.ngZone.run(() => {
        this.geocoder = new google.maps.Geocoder();
        this.isServicesInitialized = true;
        console.log('Serviços do Google Maps inicializados com sucesso');
        this.initializeMap();
        
        // Geocodifica endereços existentes
        this.locations.forEach(location => {
          if (!location.isGeocoded) {
            this.geocodeAddress(location);
          }
        });
      });
    } catch (error) {
      console.error('Erro ao inicializar serviços Google Maps:', error);
      this.initRetryCount++;
      if (this.initRetryCount <= this.maxRetries) {
        setTimeout(() => this.initializeServicesWithRetry(), 500);
      }
    }
  }

  private initializeMap(): void {
    if (!this.mapContainer || !this.isServicesInitialized) {
      return;
    }

    try {
      this.ngZone.run(() => {
        this.map = new google.maps.Map(this.mapContainer.nativeElement, {
          center: this.mapCenter,
          zoom: this.mapZoom,
          mapTypeId: this.mapType,
          styles: this.getMapStyles(),
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: true,
          scaleControl: true,
          streetViewControl: true,
          rotateControl: true,
          fullscreenControl: true
        });

        this.updateMapMarkers();
        console.log('Mapa inicializado com sucesso');
      });
    } catch (error) {
      console.error('Erro ao inicializar mapa:', error);
    }
  }

  private getMapStyles(): any[] {
    return [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'on' }]
      },
      {
        featureType: 'transit',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      }
    ];
  }

  private createCustomMarker(disabled: boolean): any {
    const fill = disabled ? '#9aa0a6' : '#d93025';
    const stroke = disabled ? '#80868b' : '#b31412';
    const inner = disabled ? '#5f6368' : '#8b1a16';
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="60" viewBox="0 0 48 60">
        <path d="M24 2c-9.94 0-18 8.06-18 18 0 11.27 10.5 24.75 18 30 7.5-5.25 18-18.73 18-30 0-9.94-8.06-18-18-18z"
              fill="${fill}" stroke="${stroke}" stroke-width="2"/>
        <circle cx="24" cy="18" r="7" fill="${inner}"/>
      </svg>`;

    const url = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
    // Escala final ~32x32 px, com a âncora no centro inferior (ponta do pin)
    return {
      url,
      scaledSize: new google.maps.Size(32, 40),
      anchor: new google.maps.Point(16, 32)
    };
  }

  selectLocation(location: any): void {
    this.selectedLocation = location;
    this.value = location.address;
    this.executeChangingEvents();
    console.log('Local selecionado:', location.name);
  }

  // Método público para adicionar um novo endereço dinamicamente
  public addAddress(address: string, name?: string): void {
    this.addLocationFromAddress(address, name);
    
    // Se o geocoder estiver disponível, faz o geocoding imediatamente
    if (this.geocoder) {
      const location = this.locations[this.locations.length - 1];
      this.geocodeAddress(location);
    }
  }

  // Método para atualizar dados de endereço
  public refreshAddresses(): void {
    this.loadAddressData();
  }

  private executeChangingEvents() {
    if (this.valueChanged) {
      let events: DynamicEvent[] = [];

      if (this.attribute.hasError && this.value) {
        this.attribute.hasError = false;
        events.push({ name: "validate" });
      }

      this.shareData(events);

      events.push({ name: "autosave", data: "silently" });
      this.emitEvents(events);
      this.initialValue = this.value;
    }
  }

  private shareData(events?: DynamicEvent[]) {
    let shareData = this.attribute.options
      .some(opt => opt.key.toLowerCase() == "sharedata" &&
        opt.value.toLowerCase() == "true");

    if (shareData && this.attribute.value) {
      if(!this.attribute.uid) {
        this.attribute.uid = performance.now().toString()
      }

      let sharingType = this.attribute.options
        .find(opt => opt.key.toLowerCase() == "sharingtype" && opt.value);

      if (sharingType && sharingType.value.toLowerCase() == "self") {
        let sharedDataKeyOpt = this.attribute.options
          .find(opt => opt.key.toLowerCase() == "SharedDataKey".toLowerCase() && opt.value);

        this.sharedDataService.setItem(
          sharedDataKeyOpt.value,
          { 
            uid: this.attribute.uid, 
            key: this.attribute.value, 
            value: this.attribute.value 
          },
          "items"
        );
      }

      if(events) {
        events.push({ name: "shareData", attribute: this.attribute })
      } else {
        this.emit({ name: "shareData", attribute: this.attribute })
      }
    }
  }

  getValueForView(): string {
    const addressesContainer = this.attribute?.attributes?.find((attr: any) => {
      return typeof attr?.key === 'string' && attr.key.toLowerCase() === 'addresses';
    });

    if (addressesContainer && Array.isArray(addressesContainer.attributes)) {
      const values: string[] = addressesContainer.attributes
        .map((child: any) => typeof child?.value === 'string' ? child.value.trim() : '')
        .filter((v: string) => v.length > 0);

      if (values.length > 0) {
        return values.join(' | ');
      }
    }

    return 'Nenhum local selecionado';
  }

  get value(): string {
    return this.attribute.value || '';
  }

  set value(input: string) {
    this.attribute.value = input;
  }

  get mapHeight(): string {
    const heightOpt = this.attribute.options?.find(opt => opt.key.toLowerCase() === 'height');
    return heightOpt ? heightOpt.value : '400px';
  }

  get mapWidth(): string {
    const widthOpt = this.attribute.options?.find(opt => opt.key.toLowerCase() === 'width');
    return widthOpt ? widthOpt.value : '100%';
  }

  get addressInfoHeader(): string {
    const custom = this.locations
      .map(loc => loc.foundAddressText)
      .find((text: string) => typeof text === 'string' && text.length > 0);

    if (custom) {
      return custom;
    }

    return `Endereços encontrados: ${this.locations.length}`;
  }

  get showAddressList(): boolean {
    try {
      const opt = this.getOptionByKey(this.attribute, 'showAddressList');
      if (!opt || typeof opt !== 'string') {
        return true;
      }
      return opt.toLowerCase() !== 'false';
    } catch {
      return true;
    }
  }
} 